import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { isInsideTelegram } from "@/lib/telegram";
import { getTelegramUser, initTelegram } from "@/lib/telegram";

interface TelegramUserInfo {
  id: number;
  firstName: string;
  username?: string;
}

interface AppContextValue {
  colorScheme: "light" | "dark";
  user: TelegramUserInfo | null;
}

const AppContext = createContext<AppContextValue | null>(null);

export function AppProvider({ children }: { children: React.ReactNode }) {
  const [colorScheme, setColorScheme] = useState<"light" | "dark">("light");

  useEffect(() => {
    initTelegram(setColorScheme);
  }, []);

  useEffect(() => {
        // Inside Telegram the theme follows Telegram exactly as before.
    const desktopSite = !isInsideTelegram() && window.matchMedia?.("(min-width: 1024px)").matches === true;
    // The desktop site sets its own theme (day / evening) in DesktopFrame.
    if (desktopSite) return;
    document.documentElement.setAttribute("data-theme", colorScheme);
  }, [colorScheme]);

  const user = useMemo<TelegramUserInfo | null>(() => {
    const tgUser = getTelegramUser();
    if (!tgUser) return null;
    return { id: tgUser.id, firstName: tgUser.first_name, username: tgUser.username };
  }, []);

  const value = useMemo(() => ({ colorScheme, user }), [colorScheme, user]);

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

export function useAppContext(): AppContextValue {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error("useAppContext must be used within AppProvider");
  return ctx;
}
